/**
 * Composed app providers – reduces cascade re-renders by:
 * 1. Single composition point instead of 6 nested providers in App.js
 * 2. Route-scoped providers (BlogProvider) only mount when needed
 * 3. Stable references via useMemo where applicable
 */
import React from 'react';
import { ThemeProvider } from './ThemeContext';
import { AccountDataProvider } from './AccountContext';
import { ItemsProvider } from './ItemsContext';
import { CartProvider } from './CartContext';
import { FavoritesProvider } from './FavoritesContext';
import { BlogProvider } from './BlogContext';
import { StudioRadioPlayerProvider } from './StudioRadioPlayerContext';
import { StudioBookmarksProvider } from './StudioBookmarksContext';
import { StudioPlaylistsProvider } from './StudioPlaylistsContext';
import { ContentActivityProvider } from './ContentActivityContext';
import { RoutePlannerProvider } from './RoutePlannerContext';

/**
 * Core providers needed for the main layout (Navbar, Footer, most routes).
 * Theme -> Account -> Items, Cart, Favorites (Cart/Favorites need Account).
 */
export function AppProviders({ children }) {
  return (
    <ContentActivityProvider>
      <ThemeProvider>
        <AccountDataProvider>
          <ItemsProvider>
            <CartProvider>
              <FavoritesProvider>
                <StudioBookmarksProvider>
                  <StudioPlaylistsProvider>
                    <StudioRadioPlayerProvider>
                      <RoutePlannerProvider>
                        {children}
                      </RoutePlannerProvider>
                    </StudioRadioPlayerProvider>
                  </StudioPlaylistsProvider>
                </StudioBookmarksProvider>
              </FavoritesProvider>
            </CartProvider>
          </ItemsProvider>
        </AccountDataProvider>
      </ThemeProvider>
    </ContentActivityProvider>
  );
}

/**
 * Blog-specific providers. Mount only when user visits blog routes.
 * Reduces memory and avoids fetching posts until needed.
 */
export function BlogRouteProvider({ children }) {
  return <BlogProvider>{children}</BlogProvider>;
}
